import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Box } from '@mui/material';
import { useAiChatPanel } from '../contexts/AiChatPanelContext';
import AiChat from '../views/Management/AiChat';

const DEFAULT_WIDTH = 420;
const MIN_WIDTH = 320;
const MAX_WIDTH = 900;
const MIN_PAGE_WIDTH = 480; // เหลือพื้นที่ให้หน้าหลักอย่างน้อยเท่านี้ ไม่ให้ลากแผงกินจนหน้าใช้ไม่ได้
const WIDTH_STORAGE_KEY = 'ai_chat_panel_width';
const DESKTOP_QUERY = '(min-width:1200px)'; // MUI lg — เท่ากับที่ MainSidebar ใช้แยก drawer มือถือ/แถบถาวร

const clampWidth = (value) => Math.round(
  Math.min(Math.max(value, MIN_WIDTH), Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, window.innerWidth - MIN_PAGE_WIDTH))),
);

// อ่านความกว้างที่เคยปรับไว้ (จำข้ามรอบเปิดเว็บ) — อ่านไม่ได้/ไม่มี = ค่าเริ่มต้น
function readSavedWidth() {
  try {
    const saved = Number(window.localStorage.getItem(WIDTH_STORAGE_KEY));
    return saved ? clampWidth(saved) : DEFAULT_WIDTH;
  } catch {
    return DEFAULT_WIDTH;
  }
}

// แผงแชท AI ที่เลื่อนออกมาทางขวาแบบ side panel ของ AI ในเบราว์เซอร์ — เปิดจากปุ่ม Coreplan AI ที่ Header
// บนจอคอมดันเนื้อหาหน้าให้หดซ้ายหลบ (padding-right ที่ #root) ไม่ทับหน้าที่กำลังทำงานอยู่ ส่วนจอเล็กลอยทับเต็มจอ
// mount ที่ root ครั้งเดียว (ดู index.js) เลยไม่รีเซ็ตแชทเวลาเปลี่ยนโมดูล
export default function AiChatPanel() {
  const { open, close } = useAiChatPanel();
  const me = useSelector((state) => state.me);
  // ทุกคนที่ login ใช้แผงได้ — สิทธิ์ดูข้อมูลแยกตามเมนูย่อยถูกบังคับที่ backend ตอนถาม (modules/aiChat)
  const allowed = Boolean(me?.userData);
  const visible = open && allowed;

  // เริ่มโหลดแชท/รายชื่อ provider ตอนเปิดแผงครั้งแรกเท่านั้น (ไม่ยิง API ทุกหน้าที่ผู้ดูแลเปิด) แล้วคง mount
  // ไว้หลังจากนั้นเพื่อให้แชทที่คุยค้างอยู่ไม่หายตอนปิดแผง
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    if (visible) setMounted(true);
  }, [visible]);

  const [width, setWidth] = useState(readSavedWidth);

  useEffect(() => {
    const root = document.getElementById('root');
    if (!root) return undefined;
    const media = window.matchMedia(DESKTOP_QUERY);
    const apply = () => {
      root.style.paddingRight = visible && media.matches ? `${width}px` : '';
    };
    apply();
    media.addListener(apply);
    return () => {
      media.removeListener(apply);
      root.style.paddingRight = '';
    };
  }, [visible, width]);

  // ลากขอบซ้ายของแผงเพื่อปรับความกว้าง — ใช้ pointer capture ให้ลากต่อเนื่องได้แม้เมาส์หลุดออกนอกแถบจับ
  // (เช่นลากผ่าน iframe/ข้อความ) ความกว้าง = ระยะจากตำแหน่งเมาส์ถึงขอบขวาจอ
  const handleResizeMove = (e) => {
    if (!e.currentTarget.hasPointerCapture(e.pointerId)) return;
    setWidth(clampWidth(window.innerWidth - e.clientX));
  };
  const handleResizeStart = (e) => {
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const handleResizeEnd = (e) => {
    e.currentTarget.releasePointerCapture(e.pointerId);
    try {
      window.localStorage.setItem(WIDTH_STORAGE_KEY, String(width));
    } catch {
      // เบราว์เซอร์บล็อก storage — แค่จำความกว้างข้ามรอบไม่ได้ ไม่กระทบการใช้งาน
    }
  };

  if (!allowed) return null;

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        bottom: 0,
        right: 0,
        width: { xs: '100vw', sm: `${width}px` },
        // สูงกว่า Header (1100) เพื่อให้ทับ Header ตอนจอเล็กที่แผงลอยเต็มจอ
        zIndex: 1180,
        bgcolor: 'background.paper',
        borderLeft: '1px solid',
        borderColor: 'divider',
        boxShadow: visible ? '-4px 0 24px rgba(0,0,0,0.12)' : 'none',
        transform: visible ? 'translateX(0)' : 'translateX(110%)',
        transition: 'transform 0.2s ease, visibility 0.2s',
        visibility: visible ? 'visible' : 'hidden',
      }}
    >
      <Box
        onPointerDown={handleResizeStart}
        onPointerMove={handleResizeMove}
        onPointerUp={handleResizeEnd}
        onPointerCancel={handleResizeEnd}
        sx={{
          display: { xs: 'none', sm: 'block' },
          position: 'absolute',
          top: 0,
          bottom: 0,
          left: -4,
          width: 8,
          cursor: 'ew-resize',
          zIndex: 1,
          touchAction: 'none',
          '&:hover, &:active': { bgcolor: 'primary.main', opacity: 0.35 },
        }}
      />
      {mounted && <AiChat compact onClose={close} active={visible} />}
    </Box>
  );
}
